import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterRenderRef, Component, DestroyRef, Injector, OnDestroy, OnInit, ViewChild, afterNextRender, computed, effect, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EligEnvConfig } from '@roosevelt/elig-ui-lib';
import { AppConfig } from '../shared/app.config';
import {
  buildSelectedSubscriber as buildSelectedSubscriberPayload,
  normalizeIsoDate,
  resolveSubscriberEnrollmentId,
  resolveSubscriberName
} from '../shared/member-feed.util';
import { PreviousRouteService } from '../shared/services/previousUrl.service';

export type AddMemberFeed = {
  clientId: any;
  subClientId: any;
  planAcronym: any;
  memberId?: any;
  pseudoSsnInd?: boolean;
  subscriberEnrollmentID?: any;
  adminPlan?: any;
  foundMember?: any;
  asOf: string;
  subscriberName?: string;
  eligibilityEffectiveDate?: any;
  receivedDate?: any;
  qualifierType?: any;
  memberBenefitType?: any;
  eligibilityStatusReason?: string;
  eligibilityStatus?: string;
};

type ClientPlanDetailsFeed = {
  planAcronym: string;
  clientSpecifiedId: string;
  subClientSpecifiedId: string;
  asOf: string;
};

type LanguageOption = {
  value: string;
  label: string;
};

type ControlLikeValue =
  | string
  | number
  | boolean
  | Array<string | number | boolean | null | undefined>
  | null
  | undefined
  | { value?: string | number | boolean | null };

type FormControlLike = {
  value: ControlLikeValue;
  setValue: (value: ControlLikeValue, options?: { emitEvent?: boolean; onlySelf?: boolean }) => void;
  updateValueAndValidity?: (options?: { onlySelf?: boolean; emitEvent?: boolean }) => void;
};

type EligAddMemberLike = {
  modifyPersonalInfoMemberDataForm?: {
    get: (controlName: string) => FormControlLike | null;
  };
  modifyContactInfoMemberDataForm?: {
    get: (controlName: string) => FormControlLike | null;
  };
};

@Component({
  selector: 'app-add-member-page',
  templateUrl: './add-member.component.html',
  styleUrls: ['./add-member.component.scss'],
  standalone: false
})
export class AddMemberComponent implements OnInit, OnDestroy {
  private static readonly MASKED_CONTACT_CONTROL_NAMES = ['phoneNumber', 'secondary', 'cell', 'faxNumber', 'phoneExtension'] as const;
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private clearDependentCountryRef: AfterRenderRef | null = null;
  private applyDependentPrefillRef: AfterRenderRef | null = null;
  private _eligAddMemberComponent?: EligAddMemberLike;

  readonly selectedAdminPlan = input('');
  readonly addFeatures = input<string[]>([]);
  readonly depMemberData = input<Record<string, unknown> | null>(null);
  readonly depMemberDataForLibrary = signal<Record<string, unknown> | null>(null);
  readonly region: string;
  readonly addMemberFeed = input<AddMemberFeed | null>(null);
  readonly languages = signal<LanguageOption[]>([]);
  readonly clientDetails = signal<unknown>(null);
  readonly clientPlanDetailsFeed = computed<ClientPlanDetailsFeed | null>(() => {
    const addMemberFeed = this.addMemberFeed();
    if (!addMemberFeed?.planAcronym || !addMemberFeed?.clientId || !addMemberFeed?.subClientId) {
      return null;
    }

    return {
      planAcronym: this.normalizeInputValue(addMemberFeed.planAcronym),
      clientSpecifiedId: this.normalizeInputValue(addMemberFeed.clientId),
      subClientSpecifiedId: this.normalizeInputValue(addMemberFeed.subClientId),
      asOf: normalizeIsoDate(addMemberFeed.asOf) || this.normalizeInputValue(addMemberFeed.asOf)
    };
  });
  private lastClientPlanDetailsKey = '';

  readonly setMemberStore = output<any>();
  @ViewChild('eligAddMemberRef')
  private set eligAddMemberComponent(component: EligAddMemberLike | undefined) {
    this._eligAddMemberComponent = component;
    if (component && this.shouldPatchDependentCountryDefault()) {
      this.scheduleDependentCountryClear();
    }
    const depMemberData = this.depMemberData();
    if (component && depMemberData && !this.depMemberDataForLibrary()) {
      if (this.areDependentFormsReady()) {
        this.coerceMaskedContactControlSetters();
        this.depMemberDataForLibrary.set(depMemberData);
      } else {
        this.scheduleDependentPrefillApply(depMemberData);
      }
    }
  }

  constructor(
    private readonly route: Router,
    private readonly location: Location,
    private readonly routerService: PreviousRouteService,
    private readonly config: AppConfig,
    private readonly http: HttpClient,
    private readonly eligEnvConfig: EligEnvConfig
  ) {
    this.region = this.config.region;

    effect(() => {
      const feed = this.addMemberFeed();
      const depMemberData = this.depMemberData();
      this.syncDependentPrefill(feed, depMemberData);

      if (feed?.subscriberEnrollmentID && !depMemberData) {
        this.scheduleDependentCountryClear();
        return;
      }

      this.clearPendingDependentCountryClear();
    });

    effect(() => {
      const clientPlanDetailsFeed = this.clientPlanDetailsFeed();
      const nextClientPlanDetailsKey = JSON.stringify(clientPlanDetailsFeed || {});
      if (nextClientPlanDetailsKey !== this.lastClientPlanDetailsKey) {
        this.lastClientPlanDetailsKey = nextClientPlanDetailsKey;
        this.clientDetails.set(null);
      }
    });

  }

  ngOnInit(): void {
    this.setMemberStore.emit({ selectedDependent: {}, dependentAdded: false });
    this.loadLanguages();
  }

  wasAddDone(action: any): void {
    if (typeof action !== 'string') {
      const selectedSubscriber = this.buildSelectedSubscriber(action);
      if (selectedSubscriber) {
        this.setMemberStore.emit({ selectedSubscriber });
      }

      const addMemberFeed = this.buildUpdatedAddMemberFeed(action);
      if (addMemberFeed) {
        this.setMemberStore.emit({ addMemberFeed });
      }

      if (action?.dependent) {
        this.setMemberStore.emit({ selectedDependent: action.dependent, dependentAdded: true });
      }

      this.clearFamilyEnrollmentMemberCache();
      this.route.navigate(['details']);
      return;
    }

    if (action === 'cancelled') {
      if ((this.routerService.getPreviousUrl() || '').indexOf('eligibility') > -1) {
        this.route.navigate(['eligibility/add-member']);
      } else {
        this.location.back();
      }
    }
  }

  private buildSelectedSubscriber(action: any): any {
    if (!action?.subscriber) {
      return null;
    }

    const addMemberFeed = this.addMemberFeed();
    const subscriber = action.subscriber;
    const subscriberEnrollmentId = resolveSubscriberEnrollmentId(subscriber) ?? addMemberFeed?.subscriberEnrollmentID;

    return {
      ...buildSelectedSubscriberPayload(subscriber, subscriberEnrollmentId),
      planAcronym: subscriber?.planAcronym ?? addMemberFeed?.planAcronym,
      clientSpecifiedIdentifier: subscriber?.clientSpecifiedIdentifier ?? addMemberFeed?.clientId,
      subclientSpecifiedIdentifier: subscriber?.subclientSpecifiedIdentifier ?? addMemberFeed?.subClientId,
      subscriberEnrollmentId,
      subscriberEnrollmentID: subscriberEnrollmentId,
      asOf: subscriber?.asOf ?? addMemberFeed?.asOf
    };
  }

  private buildUpdatedAddMemberFeed(action: any): AddMemberFeed | null {
    const addMemberFeed = this.addMemberFeed();
    if (!addMemberFeed) {
      return null;
    }

    const subscriber = this.resolveSubmitSubscriber(action);

    return {
      ...addMemberFeed,
      subscriberName: resolveSubscriberName(subscriber) || addMemberFeed.subscriberName,
      subscriberEnrollmentID: resolveSubscriberEnrollmentId(subscriber) ?? addMemberFeed.subscriberEnrollmentID
    };
  }

  private resolveSubmitSubscriber(action: any): any {
    if (!action || typeof action !== 'object') {
      return null;
    }

    if (action.subscriber) {
      return action.subscriber;
    }

    // Dependent-only payloads should not override subscriber enrollment context.
    if (action.dependent) {
      return null;
    }

    return action;
  }

  private clearFamilyEnrollmentMemberCache(): void {
    const memberDetailsState = JSON.parse(sessionStorage.getItem('member-details-state') || '{}');
    if (!memberDetailsState?.BMT) {
      return;
    }

    const nextMemberDetailsState = {
      ...memberDetailsState,
      BMT: {
        ...memberDetailsState.BMT,
        subscriberDetails: {
          ...(memberDetailsState.BMT?.subscriberDetails || {}),
          subscriberData: {
            ...(memberDetailsState.BMT?.subscriberDetails?.subscriberData || {}),
            uiSpecificData: {}
          }
        }
      }
    };

    sessionStorage.setItem('member-details-state', JSON.stringify(nextMemberDetailsState));
  }

  private loadLanguages(): void {
    this.http.get<Array<{ languagecode: string; languagename: string }>>(this.eligEnvConfig.getLanguagesUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(languages => {
        this.languages.set((languages || []).map(({ languagecode, languagename }) => ({
          value: languagecode,
          label: languagename
        })));
      });
  }

  ngOnDestroy(): void {
    this.clearPendingDependentCountryClear();
    this.clearPendingDependentPrefillApply();
  }

  onClientDetails(details: unknown): void {
    this.clientDetails.set(details);
  }

  private scheduleDependentCountryClear(): void {
    this.clearPendingDependentCountryClear();
    this.clearDependentCountryRef = afterNextRender(() => {
      this.clearDependentCountryRef = null;

      if (!this.shouldPatchDependentCountryDefault()) {
        return;
      }

      this.clearDefaultDependentCountryIfReady();
    }, { injector: this.injector });
  }

  private clearPendingDependentCountryClear(): void {
    this.clearDependentCountryRef?.destroy();
    this.clearDependentCountryRef = null;
  }

  private syncDependentPrefill(
    feed: AddMemberFeed | null,
    depMemberData: Record<string, unknown> | null
  ): void {
    this.clearPendingDependentPrefillApply();
    if (!feed || !depMemberData) {
      this.depMemberDataForLibrary.set(null);
      return;
    }

    if (this.areDependentFormsReady()) {
      this.coerceMaskedContactControlSetters();
      this.depMemberDataForLibrary.set(depMemberData);
      return;
    }

    this.depMemberDataForLibrary.set(null);
    this.scheduleDependentPrefillApply(depMemberData);
  }

  private scheduleDependentPrefillApply(depMemberData: Record<string, unknown>): void {
    this.clearPendingDependentPrefillApply();
    this.applyDependentPrefillRef = afterNextRender(() => {
      this.applyDependentPrefillRef = null;
      if (!this.addMemberFeed() || this.depMemberData() !== depMemberData) {
        return;
      }

      if (this.areDependentFormsReady()) {
        this.coerceMaskedContactControlSetters();
        this.depMemberDataForLibrary.set(depMemberData);
      }
    }, { injector: this.injector });
  }

  private clearPendingDependentPrefillApply(): void {
    this.applyDependentPrefillRef?.destroy();
    this.applyDependentPrefillRef = null;
  }

  private shouldPatchDependentCountryDefault(): boolean {
    return Boolean(this.addMemberFeed()?.subscriberEnrollmentID) && !this.depMemberData();
  }

  private areDependentFormsReady(): boolean {
    return Boolean(
      this._eligAddMemberComponent?.modifyPersonalInfoMemberDataForm &&
      this._eligAddMemberComponent?.modifyContactInfoMemberDataForm
    );
  }

  private coerceMaskedContactControlSetters(): void {
    const contactForm = this._eligAddMemberComponent?.modifyContactInfoMemberDataForm;
    if (!contactForm) {
      return;
    }

    AddMemberComponent.MASKED_CONTACT_CONTROL_NAMES.forEach(controlName => {
      const control = contactForm.get(controlName) as (FormControlLike & { __bmtSetValuePatched?: boolean }) | null;
      if (!control || control.__bmtSetValuePatched) {
        return;
      }

      const originalSetValue = control.setValue.bind(control);
      control.setValue = (value: ControlLikeValue, options?: { emitEvent?: boolean; onlySelf?: boolean }) =>
        originalSetValue(this.normalizeMaskedValue(value), options);
      control.__bmtSetValuePatched = true;

      const normalizedCurrentValue = this.normalizeMaskedValue(control.value);
      if (normalizedCurrentValue !== control.value) {
        originalSetValue(normalizedCurrentValue, { emitEvent: false });
      }
    });
  }

  private normalizeMaskedValue(value: ControlLikeValue): string {
    if (Array.isArray(value)) {
      return value.length > 0 ? this.normalizeMaskedValue(value[0] as ControlLikeValue) : '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return `${value}`;
    }

    if (value && typeof value === 'object' && 'value' in value) {
      const optionValue = value.value;
      if (typeof optionValue === 'string' || typeof optionValue === 'number' || typeof optionValue === 'boolean') {
        return `${optionValue}`;
      }
    }

    return '';
  }

  private clearDefaultDependentCountryIfReady(): boolean {
    const countryControl = this._eligAddMemberComponent?.modifyContactInfoMemberDataForm?.get('country');
    if (!countryControl) {
      return false;
    }

    if (this.shouldClearCountryControl()) {
      countryControl.setValue('');
      countryControl.updateValueAndValidity?.({ emitEvent: true });
    }

    return true;
  }

  private shouldClearCountryControl(): boolean {
    const form = this._eligAddMemberComponent?.modifyContactInfoMemberDataForm;
    const countryValue = this.readControlTextValue('country').toUpperCase();
    if (!form || countryValue !== 'US') {
      return false;
    }

    return ['homeAddressLine1', 'addressLine2', 'city', 'state', 'zip', 'county']
      .every(controlName => this.readControlTextValue(controlName) === '');
  }

  private readControlTextValue(controlName: string): string {
    const value = this._eligAddMemberComponent?.modifyContactInfoMemberDataForm?.get(controlName)?.value;
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return `${value}`.trim();
    }

    if (value && typeof value === 'object' && 'value' in value) {
      const optionValue = value.value;
      if (typeof optionValue === 'string' || typeof optionValue === 'number' || typeof optionValue === 'boolean') {
        return `${optionValue}`.trim();
      }
    }

    return '';
  }

  private normalizeInputValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
      return `${value}`.trim();
    }

    if (Array.isArray(value)) {
      return `${value[0] ?? ''}`.trim();
    }

    return '';
  }
}
